import { useEffect, useState } from "react";
import {
    Card,
    CardDescription,
    CardHeader,
    CardTitle,
    CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronRight, Home, BookOpen, BarChart } from "lucide-react";
import Installation from "./docs/getting-started/Installation";
import FirstOptimization from "./docs/getting-started/FirstOptimization";
import PriceOptimization from "./docs/features/PriceOptimization";
import Merging from "./docs/features/Merging";
import Searching from "./docs/features/Searching";
import Globale from "./docs/integrations/Globale";

type DocTopic = {
    id: string;
    title: string;
    description: string;
    icon: React.ElementType;
    component?: React.ComponentType;
    children?: DocTopic[];
};

const DOCUMENTATION: DocTopic[] = [
    {
        id: "getting-started",
        title: "Getting Started",
        description: "Learn the basics of using Optifi",
        icon: BookOpen,
        children: [
            {
                id: "installation",
                title: "Installation",
                description: "How to install and set up Optifi",
                icon: BookOpen,
                component: Installation,
            },
            {
                id: "first-optimization",
                title: "First Optimization",
                description: "Set up your first price optimization",
                icon: BarChart,
                component: FirstOptimization,
            },
        ],
    },
    {
        id: "features",
        title: "Features",
        description: "Explore Optifi's capabilities",
        icon: BarChart,
        children: [
            {
                id: "price-optimization",
                title: "Price Optimization",
                description: "AI-powered price optimization",
                icon: BarChart,
                component: PriceOptimization,
            },
            // {
            //     id: "analytics",
            //     title: "Analytics",
            //     description: "Track and analyze performance",
            //     icon: BarChart,
            //     component: Analytics,
            // },
            {
                id: "merging",
                title: "Merging",
                description: "Combine and manage product groups",
                icon: BarChart,
                component: Merging,
            },
            {
                id: "searching",
                title: "Searching & Filtering",
                description: "Managing large product catalogs",
                icon: BarChart,
                component: Searching,
            },
        ],
    },
    {
        id: "integrations",
        title: "Integrations",
        description: "Connect your store to other platforms",
        icon: BarChart,
        children: [
            {
                id: "globale",
                title: "Global-e",
                description: "Connect Optifi to Global-e",
                icon: BarChart,
                component: Globale,
            },
        ],
    },
    // {
    //     id: "faq",
    //     title: "FAQ",
    //     description: "Frequently asked questions",
    //     icon: HelpCircle,
    //     component: FAQ,
    // },
];

function findTopic(path: string[]): DocTopic | undefined {
    let current = DOCUMENTATION;
    let topic: DocTopic | undefined;

    for (const id of path) {
        topic = current.find((t) => t.id === id);
        if (!topic) return undefined;
        current = topic.children || [];
    }

    return topic;
}

export default function Support() {
    const [currentPath, setCurrentPath] = useState<string[]>([]);

    useEffect(() => {
        const hash = window.location.hash.slice(1);
        if (hash) {
            setCurrentPath(hash.split("/"));
        }
    }, []);

    const currentTopic = findTopic(currentPath);
    const Component = currentTopic?.component;
    const displayTopics = currentTopic?.children || DOCUMENTATION;

    const breadcrumbs = currentPath.map((id) => {
        const path = currentPath.slice(0, currentPath.indexOf(id) + 1);
        const topic = findTopic(path);
        return {
            id,
            title: topic?.title || id,
            path,
        };
    });

    return (
        <div className="space-y-6 p-6">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentPath([])}
                >
                    <Home className="h-4 w-4" />
                </Button>
                {breadcrumbs.map((crumb) => (
                    <div key={crumb.id} className="flex items-center">
                        <ChevronRight className="h-4 w-4" />
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setCurrentPath(crumb.path)}
                        >
                            {crumb.title}
                        </Button>
                    </div>
                ))}
            </div>

            {Component ? (
                <div className="prose prose-sm max-w-none">
                    <Component />
                </div>
            ) : (
                <>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {displayTopics.map((topic) => (
                            <Card
                                key={topic.id}
                                className="cursor-pointer transition-colors hover:bg-muted/50 min-h-[160px]"
                                onClick={() =>
                                    setCurrentPath([...currentPath, topic.id])
                                }
                            >
                                <CardHeader className="h-full">
                                    <div className="flex items-center gap-2">
                                        <topic.icon className="h-5 w-5" />
                                        <CardTitle>{topic.title}</CardTitle>
                                    </div>
                                    <CardDescription>
                                        {topic.description}
                                    </CardDescription>
                                </CardHeader>
                            </Card>
                        ))}
                    </div>
                    <Card className="mt-6">
                        <CardHeader>
                            <CardTitle>Need Additional Help?</CardTitle>
                            <CardDescription>
                                Can't find what you're looking for? Our support
                                team is here to help. Get in touch with your
                                account manager or click the button below to
                                fill out a support request.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Button
                                onClick={() =>
                                    window.open(
                                        "https://tally.so/r/nrpvro",
                                        "_blank"
                                    )
                                }
                                className="mt-2"
                            >
                                Contact Support
                            </Button>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}
